import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { Link } from "react-router-dom";
import { HiOutlinePlus } from "react-icons/hi";
import { FiBarChart2, FiEdit2, FiEye } from "react-icons/fi";
import { TbRefresh } from "react-icons/tb";
import axios from "axios";
import Pagination from "../Component/Pagination";
import "../forms/UserForm.css";
import "../SafetyObservation/safetyObservation.css";
import API_URL from "../Config";
import "./miIndex.css";
import {
  MI_EVALUATION_TYPE_OPTIONS,
  MI_STATUS_OPTIONS,
  fetchMiLocations,
  formatMiDate,
  formatMiNumber,
  formatMiPercent,
  parseMiApiError,
} from "./miIndexUtils";

const INITIAL_FILTERS = {
  location_id: "",
  status: "",
  start_date: "",
  end_date: "",
};

function MIEvaluationList({ isSidebarExpanded }) {
  const [evaluations, setEvaluations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [paginationKey, setPaginationKey] = useState(0);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const hasActiveFilters = Boolean(
    filters.location_id ||
      filters.status ||
      filters.start_date ||
      filters.end_date,
  );

  const locationOptions = useMemo(
    () =>
      (locations || []).map((location) => ({
        value: location.location_id,
        label: location.location_name,
      })),
    [locations],
  );

  const statusOptions = useMemo(
    () =>
      MI_STATUS_OPTIONS.map((status) => ({
        value: status,
        label: status,
      })),
    [],
  );

  const selectedLocationOption = useMemo(
    () =>
      locationOptions.find((option) => option.value === filters.location_id) ||
      null,
    [filters.location_id, locationOptions],
  );

  const selectedStatusOption = useMemo(
    () =>
      statusOptions.find((option) => option.value === filters.status) || null,
    [filters.status, statusOptions],
  );

  const loadLocations = useCallback(async () => {
    try {
      const data = await fetchMiLocations({ page: 1, per_page: 1000 });
      setLocations(data.locations || []);
    } catch (error) {
      showSnackbar(parseMiApiError(error, "Failed to load locations"), "error");
    }
  }, [showSnackbar]);

  const loadEvaluations = useCallback(
    async (currentPage = page, perPage = itemsPerPage, activeFilters = filters) => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/mi/evaluations`, {
          headers: {
            Authorization: localStorage.getItem("authKey") || "",
          },
          params: {
            page: currentPage,
            per_page: perPage,
            ...(activeFilters.location_id
              ? { location_id: activeFilters.location_id }
              : {}),
            ...(activeFilters.status ? { status: activeFilters.status } : {}),
            ...(activeFilters.start_date
              ? { start_date: activeFilters.start_date }
              : {}),
            ...(activeFilters.end_date ? { end_date: activeFilters.end_date } : {}),
          },
        });

        setEvaluations(response.data?.evaluations || []);
        setTotalItems(response.data?.total || 0);
      } catch (error) {
        setEvaluations([]);
        setTotalItems(0);
        showSnackbar(
          parseMiApiError(error, "Failed to load evaluations"),
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    [filters, itemsPerPage, page, showSnackbar],
  );

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    loadEvaluations(page, itemsPerPage, filters);
  }, [filters, itemsPerPage, loadEvaluations, page]);

  const handlePaginationChange = (currentPage, nextItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(nextItemsPerPage);
  };

  const handleRefresh = () => {
    if (!hasActiveFilters) {
      loadEvaluations(page, itemsPerPage, filters);
      return;
    }

    setFilters(INITIAL_FILTERS);
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleFilterChange = (fieldName, value) => {
    setFilters((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const getStatusClass = (status) => `mi-pill mi-pill--${status || "Draft"}`;

  return (
    <>
      <div
        className={`data-list-content-container page-content mi-page ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content mi-shell">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Evaluation List</PageBackTitle>
              <div className="data-list-heading-content-right-side observation-header-actions">
                <Link to="/mi-reports" className="data-list-new">
                  <Tooltip title="MI Reports" arrow>
                    <button className="new-button">
                      <FiBarChart2 className="new-plus-icon" /> Reports
                    </button>
                  </Tooltip>
                </Link>

                <Link to="/mi-add-evaluation" className="data-list-new">
                  <Tooltip title="New Evaluation" arrow>
                    <button className="new-button">
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>
                </Link>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          <div className="header-separator"></div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              className="filter-card observation-filter-card"
              style={{
                width: "100%",
                background: "#fff",
                border: "1px solid #e0e0e0",
                padding: "12px 20px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "20px",
              }}
            >
              <div className="observation-dropdown-field">
                <Select
                  options={locationOptions}
                  value={selectedLocationOption}
                  className="select-input"
                  onChange={(selectedOption) =>
                    handleFilterChange(
                      "location_id",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  placeholder="All Locations"
                  isClearable
                  isSearchable
                />
              </div>

              <div className="observation-dropdown-field">
                <Select
                  options={statusOptions}
                  value={selectedStatusOption}
                  className="select-input"
                  onChange={(selectedOption) =>
                    handleFilterChange(
                      "status",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  placeholder="All Status"
                  isClearable
                />
              </div>

              <div className="observation-filter-date">
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(event) =>
                    handleFilterChange("start_date", event.target.value)
                  }
                />
              </div>

              <div className="observation-filter-date">
                <input
                  type="date"
                  value={filters.end_date}
                  min={filters.start_date || undefined}
                  onChange={(event) =>
                    handleFilterChange("end_date", event.target.value)
                  }
                />
              </div>
            </div>
          </div>

          <div className="data-list-body">
            <div className="data-list-scroll-body-purchase_indent">
              <div className="data-list-tables">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : evaluations.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>LOCATION</th>
                        <th>DATE</th>
                        <th>EVALUATOR</th>
                        <th>TYPE</th>
                        <th>MI SCORE</th>
                        <th>PERCENTAGE</th>
                        <th className="center-alignment">STATUS</th>
                        <th className="center-alignment">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {evaluations.map((evaluation, index) => (
                        <tr key={evaluation.evaluation_id} className="hoverable-row">
                          <td className="center-alignment">
                            {(page - 1) * itemsPerPage + index + 1}
                          </td>
                          <td>
                            <div className="mi-list-primary">
                              {evaluation.location_name || "NA"}
                            </div>
                            <div className="mi-list-secondary">
                              {evaluation.location_type || "NA"}
                              {evaluation.region ? ` - ${evaluation.region}` : ""}
                            </div>
                          </td>
                          <td>{formatMiDate(evaluation.evaluation_date)}</td>
                          <td>
                            <div className="mi-list-primary">
                              {evaluation.evaluator_name || "NA"}
                            </div>
                            <div className="mi-list-secondary">
                              {evaluation.evaluator_designation || "No designation"}
                            </div>
                          </td>
                          <td>
                            {MI_EVALUATION_TYPE_OPTIONS.includes(
                              evaluation.evaluation_type,
                            )
                              ? evaluation.evaluation_type
                              : evaluation.evaluation_type || "NA"}
                          </td>
                          <td>
                            {evaluation.final_mi_score !== null &&
                            evaluation.final_mi_score !== undefined
                              ? formatMiNumber(evaluation.final_mi_score)
                              : "NA"}
                          </td>
                          <td>
                            {evaluation.percentage_obtained !== null &&
                            evaluation.percentage_obtained !== undefined
                              ? formatMiPercent(evaluation.percentage_obtained)
                              : "NA"}
                          </td>
                          <td className="center-alignment">
                            <span className={getStatusClass(evaluation.status)}>
                              {evaluation.status || "Draft"}
                            </span>
                          </td>
                          <td className="center-alignment">
                            <div className="observation-action-buttons">
                              <Link to={`/mi-evaluation-view/${evaluation.evaluation_id}`}>
                                <button className="button-bg" type="button">
                                  <FiEye /> View
                                </button>
                              </Link>
                              {evaluation.status !== "Approved" && (
                                <Link
                                  to={`/mi-edit-evaluation/${evaluation.evaluation_id}`}
                                >
                                  <button className="button-bg" type="button">
                                    <FiEdit2 /> Workflow
                                  </button>
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No Evaluations Found</div>
                  </div>
                )}
              </div>

              {totalItems > 0 && (
                <Pagination
                  key={paginationKey}
                  showPerPage={itemsPerPage}
                  total={totalItems}
                  onPaginationChange={handlePaginationChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default MIEvaluationList;
